import { CreateDevisPayload, DevisClient } from "modules/devis/types/devis.interface";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Autocomplete, Option } from "components/fields/autoCompleteField";
import { useGetClients } from "modules/article/api";
import { useDebouncedSearch } from "common/hooks/src/useDebounce";

interface SelectIntervenantsProps {
  formValues: CreateDevisPayload;
  setFormValues: (values: CreateDevisPayload) => void;
  data?: DevisClient;
}

export const SelectClient: FunctionComponent<SelectIntervenantsProps> = ({
  formValues,
  setFormValues,
  data,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedSearch(searchValue, 300);
  const { data: clients, isLoading } = useGetClients(debouncedSearch || undefined);
  const flatClients = useMemo(() => 
    clients?.pages?.map((page) => page.data).flat() || [], 
    [clients?.pages]
  );

  // Initialize with data if provided
  useEffect(() => {
    if (data?.name && !searchValue) {
      setSearchValue(data.name);
    }
  }, [data?.name, searchValue]);

  const options = useMemo(() => {
    return flatClients.map((client) => ({
      id: client._id,
      name: client.name,
    }));
  }, [flatClients]);

  const handleOptionChange = (optionValue: Option) => {
    setFormValues({
      ...formValues,
      clientId: optionValue.id,
    });
  };

  // Set search value when formValues.clientId changes and we have options
  useEffect(() => {
    if (searchValue === "" && formValues.clientId && options.length > 0) {
      const client = options.find(
        (option) => option.id === formValues.clientId
      );
      if (client) {
        setSearchValue(client.name);
      }
    }
  }, [formValues.clientId, options, searchValue]);

  if (options.length === 0 && !isLoading) return null;

  return (
    <Autocomplete
      options={options}
      onSelect={(option) => handleOptionChange(option)}
      value={[formValues.clientId]}
      label="Sélectionner un client"
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      isLoading={isLoading}
    />
  );
};
